/**
 * @file scan-feedback.ts
 * @description Звук и вибрация при скане на экране кладовщика
 * (phase19.14, решение 6 шага 0 — «вкл. по умолчанию, с переключателем»).
 *
 * Зачем файл существует: кладовщик не смотрит в экран во время скана — он
 * слышит и чувствует результат. Логика «короткий бип + 40 мс вибро» нужна и
 * экрану задания, и будущим экранам приёмки, поэтому вынесена из компонента.
 *
 * Переключатель хранится в `localStorage`: предпочтение звука принадлежит
 * устройству, а не магазину. Управляют им «Настройки склада → Рабочее место» и
 * пункт «Звук скана» в «⋯» складского документа (phase19.24 этап 1, шаг 1).
 *
 * Web Audio создаётся лениво и только по жесту пользователя (скан = ввод),
 * иначе браузер оставит контекст в состоянии `suspended`.
 */

const STORAGE_KEY = "chaiberi.admin.wms.scanFeedback";

/** Тон «успех» — короткий, чтобы не мешать серии сканов. */
const OK_FREQUENCY_HZ = 1180;
/** Тон «ошибка» — ниже и заметно длиннее: ошибку нельзя пропустить. */
const ERROR_FREQUENCY_HZ = 320;
const OK_DURATION_MS = 90;
const ERROR_DURATION_MS = 260;
const VIBRATE_OK_MS = 40;
const VIBRATE_ERROR_MS = [60, 60, 120];

let audioContext: AudioContext | null = null;

/**
 * Текущее значение настройки. Источник правды — эта переменная, а
 * `localStorage` только переживает перезагрузку: в приватном режиме и в
 * тестовой среде хранилище может молча не сохранять, и переключатель тогда
 * возвращался бы в прежнее положение сразу после нажатия.
 */
let enabledCache: boolean | null = null;

/** Включена ли обратная связь. По умолчанию — да (решение 6 шага 0). */
export function isScanFeedbackEnabled(): boolean {
  if (enabledCache === null) {
    try {
      enabledCache = localStorage.getItem(STORAGE_KEY) !== "off";
    } catch {
      // Приватный режим без localStorage — звук считаем включённым.
      enabledCache = true;
    }
  }
  return enabledCache;
}

/**
 * Подписчики на смену настройки. Нужны, потому что переключателей теперь два —
 * «Настройки склада → Рабочее место» и пункт «Звук скана» в «⋯» документа
 * (phase19.24 этап 1, шаг 1). Без общей шины второй переключатель показывал бы
 * устаревшее состояние до перезагрузки страницы.
 */
const listeners = new Set<(enabled: boolean) => void>();

/** Подписка на смену настройки; возвращает отписку (`useSyncExternalStore`). */
export function subscribeScanFeedback(
  listener: (enabled: boolean) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Переключить звук и вибро (настройка устройства, не магазина). */
export function setScanFeedbackEnabled(enabled: boolean): void {
  enabledCache = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  } catch {
    // Не критично: настройка просто не переживёт перезагрузку.
  }
  for (const listener of listeners) listener(enabled);
}

/** Успешный скан: короткий высокий бип + вибро 40 мс. */
export function playScanOk(): void {
  if (!isScanFeedbackEnabled()) return;
  beep(OK_FREQUENCY_HZ, OK_DURATION_MS);
  vibrate(VIBRATE_OK_MS);
}

/** Ошибка скана: низкий длинный сигнал + тройное вибро. */
export function playScanError(): void {
  if (!isScanFeedbackEnabled()) return;
  beep(ERROR_FREQUENCY_HZ, ERROR_DURATION_MS);
  vibrate(VIBRATE_ERROR_MS);
}

function beep(frequency: number, durationMs: number): void {
  const ctx = ensureContext();
  if (!ctx) return;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "square";
  oscillator.frequency.value = frequency;
  // Плавное затухание: резкий обрыв щёлкает в динамике телефона.
  gain.gain.setValueAtTime(0.14, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + durationMs / 1000);
}

function vibrate(pattern: number | number[]): void {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") {
    return;
  }
  try {
    navigator.vibrate(pattern);
  } catch {
    // Некоторые браузеры бросают при вызове без пользовательского жеста.
  }
}

function ensureContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  audioContext ??= new Ctor();
  if (audioContext.state === "suspended") void audioContext.resume();
  return audioContext;
}
